import React, { useEffect, useState } from 'react'
import { cn } from 'cn'
import isEqual from 'lodash/isEqual'
import { CheckCircleIcon } from '@heroicons/react/24/solid'
import FieldDescription from 'components/ui/FieldDescription'
import FieldError from 'components/ui/FieldError'
import {
  OptionGroup,
  PossibleValue,
  Props as DeepRadioGroupProps
} from 'components/ui/DeepRadioGroup'

export type Props<T extends PossibleValue = string> = Omit<
  DeepRadioGroupProps<T>,
  'columns'
>

function chunk<T>(arr: T[], size: number): T[][] {
  const result: T[][] = []
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size))
  }
  return result
}

function CardButton<T extends PossibleValue>({
  group,
  isActive,
  hasError,
  onChange
}: {
  group: OptionGroup<T>
  isActive: boolean
  hasError: boolean
  onChange: (value: T) => void
}) {
  return (
    <button
      type="button"
      className={cn(
        'relative flex items-center rounded-xl border px-4 py-3 transition-all text-left',
        {
          'border-red-500': hasError && !isActive,
          'border-orange-500 bg-orange-50 shadow-xs': isActive,
          'border-gray-300 bg-white hover:border-gray-400':
            !isActive && !hasError
        }
      )}
      onClick={() => onChange(group.value)}
    >
      {isActive ? (
        <CheckCircleIcon className="absolute top-3 right-3 h-5 w-5 text-orange-500" />
      ) : (
        <div className="absolute top-3 right-3 h-5 w-5 rounded-full border-2 border-gray-300" />
      )}
      <span
        className={cn('text-sm font-semibold pr-6', {
          'text-gray-900': isActive,
          'text-gray-700': !isActive
        })}
      >
        {group.label}
      </span>
    </button>
  )
}

export default function DeepCardRadioGroup<T extends PossibleValue = string>({
  label,
  description,
  error,
  optionGroups,
  value,
  onChange,
  questionId = 'deep-card-radio-group',
  newDesign = false
}: Props<T>) {
  const [columns, setColumns] = useState(3)

  useEffect(() => {
    const mql = window.matchMedia('(min-width: 640px)')
    setColumns(mql.matches ? 3 : 2)
    const handler = (e: MediaQueryListEvent) => setColumns(e.matches ? 3 : 2)
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [])

  const activeGroup = optionGroups.find(
    (group) =>
      isEqual(group.value, value) ||
      group.options.some((o) => isEqual(o.value, value))
  )

  const rows = chunk(optionGroups, columns)

  return (
    <div
      aria-describedby={`description-${questionId}`}
      aria-labelledby={`label-${questionId}`}
      className="w-full"
      role="radiogroup"
    >
      <div className="mb-3">
        <p
          className={cn('block', {
            'text-base font-medium text-gray-700': !newDesign,
            'font-theme-heading font-bold text-gray-900': newDesign
          })}
          id={`label-${questionId}`}
        >
          {label}
        </p>
        <FieldDescription newDesign={newDesign}>{description}</FieldDescription>
        <FieldError>{error}</FieldError>
      </div>
      <div className="flex flex-col gap-3">
        {rows.map((row) => {
          const activeIndexInRow = activeGroup
            ? row.findIndex((g) => g === activeGroup)
            : -1
          const showSubcategories =
            activeIndexInRow >= 0 && activeGroup!.options.length > 0
          const arrowLeftPercent = ((activeIndexInRow + 0.5) / columns) * 100

          return (
            <React.Fragment key={row.map((g) => String(g.value)).join('-')}>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {row.map((group) => (
                  <CardButton
                    key={String(group.value)}
                    group={group}
                    hasError={!!error}
                    isActive={
                      isEqual(group.value, value) ||
                      group.options.some((o) => isEqual(o.value, value))
                    }
                    onChange={onChange}
                  />
                ))}
              </div>
              {showSubcategories && activeGroup && (
                <div className="relative mt-1 rounded-xl border border-orange-200 bg-orange-50 p-4">
                  <div
                    className="absolute top-[-9px] h-4 w-4 rotate-45 border-l border-t border-orange-200 bg-orange-50"
                    style={{ left: `calc(${arrowLeftPercent}% - 8px)` }}
                  />
                  <p className="text-xs font-medium text-gray-500 mb-2.5">
                    What kind of {String(activeGroup.label).toLowerCase()}?
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {activeGroup.options.map((option) => {
                      const selected = isEqual(option.value, value)
                      return (
                        <button
                          key={String(option.value)}
                          type="button"
                          className={cn(
                            'rounded-full px-4 py-1.5 text-sm font-medium transition-colors',
                            selected
                              ? 'bg-orange-500 text-white shadow-xs'
                              : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                          )}
                          onClick={() => onChange(option.value)}
                        >
                          {option.label}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}
            </React.Fragment>
          )
        })}
      </div>
    </div>
  )
}
